import { useRef } from 'react'
import type * as React from 'react'

export type ClickAnimationOptions = {
  duration?: 100 | 200
  scale?: 0.8 | 0.9 | 0.95
}

/**
 * CSS press-scale animation with minimum visible duration.
 * Toggles a data attribute via pointer events + a minimum-duration timer.
 * The attribute survives React className updates during the press animation.
 */
export function useClickAnimation(options?: ClickAnimationOptions) {
  const duration = options?.duration ?? 100
  const scale = options?.scale ?? 0.9
  const genRef = useRef(0)

  const sizeClass =
    scale === 0.8 ? 'click-xs' : scale === 0.95 ? 'click-md' : 'click-sm'
  const speedClass = duration === 200 ? 'click-slow' : 'click-fast'

  const handlers = {
    onPointerDown(e: React.PointerEvent) {
      const el = e.currentTarget as HTMLElement
      const gen = ++genRef.current
      el.dataset.clickPressed = ''

      let minDone = false
      let released = false

      const tryRelease = () => {
        if (minDone && released && gen === genRef.current) {
          delete el.dataset.clickPressed
        }
      }

      setTimeout(() => {
        minDone = true
        tryRelease()
      }, duration)

      const onUp = () => {
        released = true
        tryRelease()
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
      }
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
  }

  return { handlers, className: `clickable ${speedClass} ${sizeClass}` }
}
